import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Users, UserPlus, Key, Eye, X, CheckCircle, ShieldAlert } from 'lucide-react';
import './AdminPages.css';

const AdminEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empResults, setEmpResults] = useState(null);

  // Form Fields
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', first_name: '', last_name: '', office_id: '', phone_number: '', department_id: ''
  });
  const [newPassword, setNewPassword] = useState('');

  const { addToast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [empRes, deptRes] = await Promise.all([
        api.get('/employees/'),
        api.get('/departments/')
      ]);
      setEmployees(empRes.data.results || empRes.data);
      setDepartments(deptRes.data.results || deptRes.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load employee list.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employees/', formData);
      addToast('Employee account created successfully!', 'success');
      setShowCreateModal(false);
      setFormData({ username: '', email: '', password: '', first_name: '', last_name: '', office_id: '', phone_number: '', department_id: '' });
      fetchData();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.username?.[0] || err.response?.data?.office_id?.[0] || 'Failed to create employee.';
      addToast(msg, 'error');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      addToast('Password must be at least 6 characters.', 'error');
      return;
    }
    try {
      await api.post(`/employees/${selectedEmp.id}/reset-password/`, { new_password: newPassword });
      addToast(`Password reset successfully for ${selectedEmp.user.username}`, 'success');
      setShowResetModal(false);
      setNewPassword('');
    } catch (err) {
      console.error(err);
      addToast('Failed to reset password.', 'error');
    }
  };

  const handleViewResults = async (emp) => {
    setSelectedEmp(emp);
    setShowResultsModal(true);
    try {
      const res = await api.get(`/employees/${emp.id}/onboarding-results/`);
      setEmpResults(res.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load onboarding results.', 'error');
    }
  };

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          <div className="admin-page-header">
            <div>
              <h1 className="admin-page-title">Employee Roster & Account Management</h1>
              <p className="admin-page-subtitle">Register new employees, assign departments, reset passwords, and view onboarding answers.</p>
            </div>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <UserPlus size={18} /> Add New Employee
            </button>
          </div>

          {loading ? <LoadingSpinner label="Loading employee roster..." /> : (
            <div className="card admin-table-card">
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name & Username</th>
                      <th>Office ID</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Joining Date</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => (
                      <tr key={emp.id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-main, #f8fafc)' }}>
                          <div>{emp.user?.first_name ? `${emp.user.first_name} ${emp.user.last_name || ''}` : emp.user?.username}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #94a3b8)', fontWeight: 400 }}>@{emp.user?.username} • {emp.user?.email}</div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{emp.office_id}</td>
                        <td>
                          <span className="badge badge-info">{emp.department_detail?.name || 'Unassigned'}</span>
                        </td>
                        <td>
                          <span className={`badge ${emp.is_active ? 'badge-success' : 'badge-warning'}`}>
                            {emp.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted, #94a3b8)' }}>{emp.joining_date}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div className="admin-action-btns" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary btn-sm" title="View Onboarding Results" onClick={() => handleViewResults(emp)}>
                              <Eye size={14} /> Results
                            </button>
                            <button className="btn btn-secondary btn-sm" title="Reset Password" onClick={() => { setSelectedEmp(emp); setShowResetModal(true); }}>
                              <Key size={14} /> Reset Pass
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal: Create Employee */}
          {showCreateModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Register New Employee</h2>
                  <button onClick={() => setShowCreateModal(false)}><X size={20} /></button>
                </div>
                <form onSubmit={handleCreateEmployee}>
                  <div className="form-group">
                    <label className="form-label">Username *</label>
                    <input type="text" className="form-input" required value={formData.username} onChange={e => setFormData({...formData, username: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Initial Password *</label>
                    <input type="password" className="form-input" required minLength={6} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input type="text" className="form-input" value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input type="text" className="form-input" value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Office ID *</label>
                      <input type="text" className="form-input" required placeholder="EMP-1002" value={formData.office_id} onChange={e => setFormData({...formData, office_id: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <select className="form-select" value={formData.department_id} onChange={e => setFormData({...formData, department_id: e.target.value})}>
                        <option value="">-- Select Department --</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create Employee</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: Reset Password */}
          {showResetModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Reset Password for {selectedEmp?.user?.username}</h3>
                  <button onClick={() => setShowResetModal(false)}><X size={20} /></button>
                </div>
                <form onSubmit={handleResetPassword}>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input type="password" className="form-input" required minLength={6} placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowResetModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Save New Password</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal: View Onboarding Results */}
          {showResultsModal && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: '32px', maxWidth: '700px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Onboarding Answers: {selectedEmp?.user?.username}</h2>
                  <button onClick={() => setShowResultsModal(false)}><X size={20} /></button>
                </div>
                
                {!empResults ? <LoadingSpinner label="Fetching answers..." /> : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '60vh', overflowY: 'auto' }}>
                    <div>
                      <h4 style={{ color: '#4f46e5', marginBottom: '10px' }}>Predefined MCQ Answers</h4>
                      {empResults.predefined_mcq_submissions?.length === 0 ? <p style={{ fontSize: '0.85rem', color: '#64748b' }}>No answers submitted yet.</p> : (
                        empResults.predefined_mcq_submissions.map((sub, idx) => (
                          <div key={idx} style={{ padding: '10px', background: '#f8fafc', borderRadius: '8px', marginBottom: '6px', fontSize: '0.85rem' }}>
                            <div style={{ fontWeight: 600 }}>{sub.question}</div>
                            <div style={{ color: '#059669', marginTop: '2px' }}>Submitted: {sub.submitted_answer}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminEmployees;
